import Dexie, { Table } from 'dexie';
import { SKU, Log, Settings } from '../types';
import { supabaseSync } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { createBackup } from './backup';

export class MusinsaWMSDatabase extends Dexie {
  skus!: Table<SKU>;
  logs!: Table<Log>;
  settings!: Table<Settings>;

  constructor() {
    super('MusinsaWMS');

    this.version(4).stores({
      skus: 'id, styleNo, color, size, status, expectedQuantity, actualQuantity, progress, lastScanned, lastModified, userId',
      logs: '++id, timestamp, type, skuId',
      settings: 'id',
    });

    // SKU 생성 시 타임스탬프 자동 설정
    this.skus.hook('creating', (primKey, obj) => {
      const now = new Date();
      if (!obj.lastScanned) obj.lastScanned = null;
      if (!obj.lastModified) obj.lastModified = now;
      if (!obj.actualQuantity) obj.actualQuantity = 0;
      if (!obj.progress) obj.progress = 0;
      if (!obj.status) obj.status = 'pending';
      return obj;
    });

    // SKU 업데이트 시 타임스탬프 자동 갱신 및 서버 동기화
    this.skus.hook('updating', async (modifications, primKey, obj) => {
      modifications.lastModified = new Date();
      if (!obj.lastScanned && !modifications.lastScanned) {
        modifications.lastScanned = null;
      }

      const updatedObj = { ...obj, ...modifications };
      if (navigator.onLine) {
        try {
          await supabaseSync.syncToServer(updatedObj);
        } catch (error: any) {
          console.error('서버 동기화 실패:', error.message);
          toast.error('서버 동기화에 실패했습니다. 데이터는 로컬에 저장되었습니다.');
        }
      } else {
        toast.warn('오프라인 상태: 데이터가 로컬에만 저장되었습니다.');
      }

      return modifications;
    });
  }
}

export const db = new MusinsaWMSDatabase();

// 초기 설정값 설정
export async function initializeSettings(attempt: number = 1, maxAttempts: number = 3): Promise<void> {
  try {
    const settings = await db.settings.get('default');
    if (!settings) {
      await db.settings.add({
        id: 'default',
        autoSaveInterval: 5 * 60 * 1000, // 5분
        skuBatchSize: 20,
        showNotifications: true,
        deleteLogsAfterDays: 30,
        adminPassword: 'admin123',
        autoBackupEnabled: false,
        autoBackupInterval: 24 * 60 * 60 * 1000, // 24시간
        lastBackupDate: new Date(0),
      });
      console.log('기본 설정이 초기화되었습니다.');
    }
  } catch (error: any) {
    console.error(`설정 초기화 실패 (시도 ${attempt}/${maxAttempts}):`, error.message);
    if (attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return initializeSettings(attempt + 1, maxAttempts);
    }
    toast.error('설정 초기화에 실패했습니다. 기본값을 사용합니다.');
  }
}

// 오래된 로그 삭제
async function deleteOldLogs() {
  try {
    const settings = await db.settings.get('default');
    if (!settings || !settings.deleteLogsAfterDays) return;

    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - settings.deleteLogsAfterDays);

    const deletedCount = await db.logs
      .where('timestamp')
      .below(cutoffDate)
      .delete();

    if (deletedCount > 0) {
      console.log(`${deletedCount}개의 오래된 로그가 삭제되었습니다.`);
    }
  } catch (error: any) {
    console.error('오래된 로그 삭제 실패:', error.message);
    toast.error('오래된 로그 삭제에 실패했습니다.');
  }
}

// 자동 백업 설정
async function setupAutoBackup() {
  try {
    const settings = await db.settings.get('default');
    if (!settings || !settings.autoBackupEnabled || !settings.autoBackupInterval) return;

    const intervalId = setInterval(async () => {
      try {
        const currentSettings = await db.settings.get('default');
        if (!currentSettings?.autoBackupEnabled) {
          clearInterval(intervalId);
          return;
        }

        const now = new Date();
        const lastBackup = currentSettings.lastBackupDate
          ? new Date(currentSettings.lastBackupDate)
          : new Date(0);

        if (now.getTime() - lastBackup.getTime() >= currentSettings.autoBackupInterval) {
          const backup = await createBackup();
          await db.settings.update('default', { lastBackupDate: now });
          console.log('자동 백업 완료:', backup);
          toast.success('자동 백업이 완료되었습니다.');
        }
      } catch (error: any) {
        console.error('자동 백업 실패:', error.message);
        toast.error('자동 백업에 실패했습니다.');
      }
    }, settings.autoBackupInterval);
  } catch (error: any) {
    console.error('자동 백업 설정 실패:', error.message);
    toast.error('자동 백업 설정에 실패했습니다.');
  }
}

// 초기화 및 주기적 로그 삭제 설정
async function initialize() {
  await initializeSettings();
  await deleteOldLogs();
  await setupAutoBackup();

  // 매일 오래된 로그 삭제
  setInterval(deleteOldLogs, 24 * 60 * 60 * 1000); // 24시간
}

initialize();